import { useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import {
    Button,
    Dimmer,
    Form,
    Header,
    Icon,
    Label,
    Input,
    Loader,
    Message,
    Segment,
    Table,
} from 'semantic-ui-react';

import * as Constants from '../../../Constants/Constant';
import { useAppContext } from '../../../Contexts/AppContext';
import Functions from '../../../Functions';
import {
    getGen1StatusLabelMeta,
    getGen1StatusSelectOptions,
} from '../gen1Status';

const defaultFilters = {
    status: '',
    trunkingOrderNumber: '',
    contactName: '',
    dealerOrderNumber: '',
    orderNumber: '',
    systemId: '',
    requestedDateFrom: '',
    requestedDateTo: '',
};

const SEARCH_DEBOUNCE_MS = 400;

export default function Gen1History(props) {
    /** 行クリック時に Gen1 親へ渡す。Detail 表示は親の detailOrderId で制御する。 */
    const { onOpenDetail } = props;
    const { Reducer, Actions } = useAppContext();
    const [filters, setFilters] = useState(defaultFilters);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    const statusOptions = useMemo(() => getGen1StatusSelectOptions(), []);

    const searchParams = useMemo(
        () => ({
            trunkingOrderNumber: filters.trunkingOrderNumber,
            contactName: filters.contactName,
            dealerOrderNumber: filters.dealerOrderNumber,
            orderNumber: filters.orderNumber,
            systemId: filters.systemId,
            requestedDateFrom: filters.requestedDateFrom,
            requestedDateTo: filters.requestedDateTo,
        }),
        [
            filters.trunkingOrderNumber,
            filters.contactName,
            filters.dealerOrderNumber,
            filters.orderNumber,
            filters.systemId,
            filters.requestedDateFrom,
            filters.requestedDateTo,
        ]
    );

    const [debouncedSearch] = useDebounce(searchParams, SEARCH_DEBOUNCE_MS);

    useEffect(() => {
        if (!Reducer.csrftoken) return;
        Actions.fetchGen1Orders(
            Reducer.csrftoken,
            {
                status: filters.status,
                trunkingOrderNumber: debouncedSearch.trunkingOrderNumber,
                contactName: debouncedSearch.contactName,
                dealerOrderNumber: debouncedSearch.dealerOrderNumber,
                orderNumber: debouncedSearch.orderNumber,
                systemId: debouncedSearch.systemId,
                requestedDateFrom: debouncedSearch.requestedDateFrom,
                requestedDateTo: debouncedSearch.requestedDateTo,
            },
            page,
            pageSize
        );
    }, [
        Reducer.csrftoken,
        filters.status,
        debouncedSearch.trunkingOrderNumber,
        debouncedSearch.contactName,
        debouncedSearch.dealerOrderNumber,
        debouncedSearch.orderNumber,
        debouncedSearch.systemId,
        debouncedSearch.requestedDateFrom,
        debouncedSearch.requestedDateTo,
        page,
        pageSize,
    ]);

    function onRowClick(orderId) {
        if (onOpenDetail) {
            onOpenDetail(orderId);
        }
    }

    const orders = Reducer.gen1Orders ?? [];
    const totalCount = Reducer.gen1OrdersTotalCount ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    const safePage = Math.min(page, totalPages);

    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [page, totalPages]);

    return (
        <>
            <Dimmer active={Reducer.gen1OrdersFetching} inverted page>
                <Loader>Loading...</Loader>
            </Dimmer>
            <Header
                as="h2"
                icon="list"
                content="Gen1 Trunking Order Form - History"
            />
            {Reducer.gen1OrdersFetchErrorMessage && (
                <Message
                    error
                    onDismiss={() =>
                        Actions.gen1OrdersListFetchError(false, '')
                    }
                >
                    {Reducer.gen1OrdersFetchErrorMessage}
                </Message>
            )}
            <Segment>
                <Header content="Search Condition" icon="filter" />
                <Form
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'flex-end',
                            justifyContent: 'space-between',
                            gap: 18,
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'flex-end',
                                gap: 18,
                            }}
                        >
                            <Form.Field style={{ margin: 0, minWidth: 140 }}>
                                <label>Status</label>
                                <Form.Select
                                    options={statusOptions}
                                    value={filters.status}
                                    onChange={(evn, data) => {
                                        setFilters((prev) => ({
                                            ...prev,
                                            status: data.value,
                                        }));
                                        setPage(1);
                                    }}
                                    placeholder="All"
                                />
                            </Form.Field>
                            <Form.Field style={{ margin: 0, minWidth: 160 }}>
                                <label>Trunking Order Number</label>
                                <Input
                                    placeholder="Trunking Order Number"
                                    value={filters.trunkingOrderNumber}
                                    name="trunkingOrderNumber"
                                    onChange={(evn, data) => {
                                        setFilters((prev) => ({
                                            ...prev,
                                            [data.name]: data.value,
                                        }));
                                        setPage(1);
                                    }}
                                />
                            </Form.Field>
                            <Form.Field style={{ margin: 0, minWidth: 160 }}>
                                <label>Contact Name</label>
                                <Input
                                    placeholder="Contact Name"
                                    value={filters.contactName}
                                    name="contactName"
                                    onChange={(evn, data) => {
                                        setFilters((prev) => ({
                                            ...prev,
                                            [data.name]: data.value,
                                        }));
                                        setPage(1);
                                    }}
                                />
                            </Form.Field>
                            <Form.Field style={{ margin: 0, minWidth: 160 }}>
                                <label>Dealer Order Number</label>
                                <Input
                                    placeholder="Dealer Order Number"
                                    value={filters.dealerOrderNumber}
                                    name="dealerOrderNumber"
                                    onChange={(evn, data) => {
                                        setFilters((prev) => ({
                                            ...prev,
                                            [data.name]: data.value,
                                        }));
                                        setPage(1);
                                    }}
                                />
                            </Form.Field>
                            <Form.Field style={{ margin: 0, minWidth: 160 }}>
                                <label>Sales Order Number (SO#)</label>
                                <Input
                                    placeholder="Sales Order Number (SO#)"
                                    value={filters.orderNumber}
                                    name="orderNumber"
                                    onChange={(evn, data) => {
                                        setFilters((prev) => ({
                                            ...prev,
                                            [data.name]: data.value,
                                        }));
                                        setPage(1);
                                    }}
                                />
                            </Form.Field>
                            <Form.Field style={{ margin: 0, minWidth: 160 }}>
                                <label>System ID</label>
                                <Input
                                    placeholder="System ID"
                                    value={filters.systemId}
                                    name="systemId"
                                    onChange={(evn, data) => {
                                        setFilters((prev) => ({
                                            ...prev,
                                            [data.name]: data.value,
                                        }));
                                        setPage(1);
                                    }}
                                />
                            </Form.Field>
                        </div>
                        <Button
                            type="button"
                            basic
                            onClick={() => {
                                setFilters(defaultFilters);
                                setPage(1);
                            }}
                            style={{ whiteSpace: 'nowrap' }}
                        >
                            <Icon name="undo" />
                            Clear
                        </Button>
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'flex-end',
                            gap: 18,
                            marginTop: 12,
                        }}
                    >
                        <Form.Field style={{ margin: 0, minWidth: 180 }}>
                            <label>Requested Date From</label>
                            <Input
                                type="date"
                                name="requestedDateFrom"
                                value={filters.requestedDateFrom}
                                onChange={(evn, data) => {
                                    setFilters((prev) => ({
                                        ...prev,
                                        [data.name]: data.value,
                                    }));
                                    setPage(1);
                                }}
                            />
                        </Form.Field>
                        <span
                            style={{
                                color: 'rgba(0,0,0,0.6)',
                                paddingBottom: 8,
                            }}
                        >
                            -
                        </span>
                        <Form.Field style={{ margin: 0, minWidth: 180 }}>
                            <label>Requested Date To</label>
                            <Input
                                type="date"
                                name="requestedDateTo"
                                value={filters.requestedDateTo}
                                onChange={(evn, data) => {
                                    setFilters((prev) => ({
                                        ...prev,
                                        [data.name]: data.value,
                                    }));
                                    setPage(1);
                                }}
                            />
                        </Form.Field>
                    </div>
                </Form>
            </Segment>

            <Table striped celled selectable>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell content="Status" />
                        <Table.HeaderCell content="Trunking Order Number" />
                        <Table.HeaderCell content="Contact Name" />
                        <Table.HeaderCell content="Dealer Order Number" />
                        <Table.HeaderCell content="Sales Order Number (SO#)" />
                        <Table.HeaderCell content="System Type" />
                        <Table.HeaderCell content="System ID" />
                        <Table.HeaderCell content="Requested Date" />
                        <Table.HeaderCell content="Completed Date" />
                        <Table.HeaderCell collapsing />
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {orders.length === 0 ? (
                        <Table.Row>
                            <Table.Cell colSpan={10} textAlign="center">
                                <Message info>
                                    No orders found. You can submit an order
                                    from the Application tab.
                                </Message>
                            </Table.Cell>
                        </Table.Row>
                    ) : (
                        orders.map((order) => {
                            const systemId = String(
                                order.SystemId ?? ''
                            ).trim();
                            return (
                                <Table.Row
                                    key={order.id}
                                    onClick={() => onRowClick(order.id)}
                                    style={{
                                        cursor: onOpenDetail
                                            ? 'pointer'
                                            : 'default',
                                    }}
                                >
                                    <Table.Cell>
                                        {(() => {
                                            const m = getGen1StatusLabelMeta(
                                                order?.Status
                                            );
                                            if (!m.text) return '-';
                                            return (
                                                <Label ribbon color={m.color}>
                                                    <p style={{ width: 85 }}>
                                                        <Icon name={m.icon} />
                                                        {m.text}
                                                    </p>
                                                </Label>
                                            );
                                        })()}
                                    </Table.Cell>
                                    <Table.Cell
                                        content={
                                            order.id > 0
                                                ? String(order.id)
                                                : '-'
                                        }
                                    />
                                    <Table.Cell
                                        content={order.ContactName || '-'}
                                    />
                                    <Table.Cell
                                        content={
                                            (
                                                order.DealerOrderNumber ?? ''
                                            ).trim() !== ''
                                                ? String(
                                                      order.DealerOrderNumber
                                                  ).trim()
                                                : '-'
                                        }
                                    />
                                    <Table.Cell
                                        content={
                                            (order.OrderNumber ?? '').trim() !==
                                            ''
                                                ? String(
                                                      order.OrderNumber
                                                  ).trim()
                                                : '-'
                                        }
                                    />
                                    <Table.Cell
                                        content={order.SystemType || '-'}
                                    />
                                    <Table.Cell
                                        content={
                                            systemId &&
                                            systemId.toLowerCase() !== 'new'
                                                ? systemId
                                                : '-'
                                        }
                                    />
                                    <Table.Cell
                                        content={
                                            Functions.dateForm(
                                                order.RequestedDate
                                            ) || '-'
                                        }
                                    />
                                    <Table.Cell
                                        content={
                                            Functions.dateForm(
                                                order.CompletedDate
                                            ) || '-'
                                        }
                                    />
                                    <Table.Cell collapsing>
                                        <Button
                                            type="button"
                                            icon="file"
                                            color="blue"
                                            aria-label="View"
                                            onClick={(evn) => {
                                                evn.stopPropagation();
                                                onRowClick(order.id);
                                            }}
                                        />
                                    </Table.Cell>
                                </Table.Row>
                            );
                        })
                    )}
                </Table.Body>
            </Table>

            <Segment
                basic
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    paddingTop: 0,
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 14,
                        flexWrap: 'wrap',
                    }}
                >
                    <span style={{ color: 'rgba(0,0,0,0.6)' }}>
                        Total: {totalCount} order{totalCount !== 1 ? 's' : ''}
                    </span>
                    <Form.Field inline style={{ margin: 0 }}>
                        <label>Per page</label>
                        <Form.Select
                            options={[10, 20, 50].map((n) => ({
                                key: n,
                                value: n,
                                text: String(n),
                            }))}
                            value={pageSize}
                            onChange={(evn, data) => {
                                setPageSize(Number(data.value));
                                setPage(1);
                            }}
                            style={{ width: 90 }}
                        />
                    </Form.Field>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ color: 'rgba(0,0,0,0.6)' }}>
                        Page {safePage} of {totalPages}
                    </span>
                    <Button
                        type="button"
                        basic
                        size="small"
                        icon
                        aria-label="Previous page"
                        disabled={safePage <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                        <Icon name="chevron left" />
                    </Button>
                    <Button
                        type="button"
                        basic
                        size="small"
                        icon
                        aria-label="Next page"
                        disabled={safePage >= totalPages}
                        onClick={() =>
                            setPage((p) => Math.min(totalPages, p + 1))
                        }
                    >
                        <Icon name="chevron right" />
                    </Button>
                </div>
            </Segment>
        </>
    );
}
